'use client';

import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogOverlay, 
  DialogPortal,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import CourseComposer from '@/modules/lcms/content-courses/components/composer/course-composer';
import { courseContentService } from '@/modules/lcms/content-courses/services/courseContentService';
import { ContentCourse } from '@/modules/lcms/content-courses/schemas/schemas';

interface FullscreenCourseEditDialogProps {
  courseId: string;
  onClose?: () => void;
}

export function FullscreenCourseEditDialog({ courseId, onClose }: FullscreenCourseEditDialogProps) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [courseData, setCourseData] = useState<ContentCourse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setIsLoading(true);
        const data = await courseContentService.getCourse(courseId);
        setCourseData(data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch course data');
        console.error('Error fetching course data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId]);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogOverlay className="bg-gray-800/80 backdrop-blur-sm" />
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="flex h-screen w-screen">
            <DialogContent 
              className="m-0 p-0 border-0 bg-white dark:bg-gray-900 max-w-none w-full h-full shadow-none"
              onInteractOutside={(e) => e.preventDefault()} // Prevent closing when clicking outside
              onEscapeKeyDown={(e) => e.preventDefault()} // Prevent closing with Esc key
            >
              <DialogTitle className="sr-only">Edit Course</DialogTitle>
              {isLoading ? (
                <div className="flex items-center justify-center h-full w-full">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-300">{t('common.loading')}</p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center h-full w-full">
                  <div className="text-center">
                    <div className="text-red-600 mb-4">{error}</div>
                    <Button onClick={() => window.location.reload()}>
                      {t('common.retry')}
                    </Button>
                    <Button variant="outline" onClick={handleClose} className="ml-2">
                      {t('common.goBack')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="h-full w-full flex flex-col">
                  {/* Header with close button */}
                  <div className="flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={handleClose}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                        <span className="ml-2">Close</span>
                      </Button>
                    </div>
                    
                    <div className="text-center flex-1">
                      <h1 className="font-bold truncate">{courseData?.name || 'Edit Course'}</h1>
                    </div>
                    
                    <div className="w-20"></div> {/* Spacer to balance the header */}
                  </div>
                  
                  {/* Course Composer Component */}
                  <div className="flex-1 overflow-auto">
                    <CourseComposer courseId={courseId} itemId="" itemType="course" />
                  </div>
                </div>
              )}
            </DialogContent>
          </div>
        </div>
      </DialogPortal>
    </Dialog>
  );
} 